import { EMetricTypes } from '@src/emp-3.0/types';
import { occurrenceTypeOptions, pairingLogicOptions } from '@src/pages/metric-templates/constants';
import {
  calculateOptions,
  CalculateTypeOptions,
} from '@src/emp-3.0/components/Metrics/IndividualMetric/MetricBuilder/MetricDefinition';
import { categoryTypeMap } from '@src/emp-3.0/components/custom-dimension/custom-dimension-panel/constant';

export const DEFAULT_VALUE = 'All';

// Represents the dividing line
export const DIVIDER = 'DIVIDER';

// Helper function to generate map from options
const createOptionMap = (options) =>
  options.reduce((map, option) => {
    map[option.value] = option.label;
    return map;
  }, {});

export const metricTypeMapping = {
  // metric type mapping
  [EMetricTypes.DURATION]: 'Duration',
  [EMetricTypes.CONVERSION]: 'Conversion',
  [EMetricTypes.SINGLE_EVENT]: 'Event Aggregation',
  [EMetricTypes.RATIO_METRIC]: 'Ratio Metric',
};

// Mapping of all values in the deployment page
export const deploymentValuesMap = {
  // pairing-logic field mapping
  ...createOptionMap(pairingLogicOptions),
  // occurrence-type mapping
  ...createOptionMap(occurrenceTypeOptions),
  // calculate mapping
  ...createOptionMap(calculateOptions),
  // calculate-type mapping
  ...createOptionMap(CalculateTypeOptions),
  // category mapping
  ...categoryTypeMap,
  // deployment type mapping
  metrics: 'Metric',
  mapped_events: 'Mapped Event',
  data_transform: 'Dimension',
  custom_dimension: 'Dimension',
  flow: 'Flow',
};

// Mapping of all keys in the deployment page
export const deploymentKeysMap = {
  metrics: {
    name: 'Metric Name',
    category: 'Category',
    description: 'Description',
    metrics_type: 'Metric Type',
    first_event: 'Initial Event',
    second_event: 'Follow-up Event',
    pairing_logic: 'Pairing Logic',
    occurrence_type: 'Occurrence Type',
    event: 'Event',
    calculation: 'Calculation',
    attribute: 'Tag Key',
    is_higher_better: 'Preferred Direction',
    unit: 'Unit Setting', // HP-9387 change this wording
    numerator: 'Numerator',
    denominator: 'Denominator',
    without_event: 'Without Event',
  },
  custom_dimension: {
    mapped_event_names: 'Mapping Rules',
    display_name: 'Dimension Name',
    category: 'Dimension Category',
  },
  mapped_events: {
    related_metrics: 'Related Metrics',
    rules: 'Mapping Rules',
    event_name: 'Mapped Event',
    event_category: 'Event Category',
    description: 'Description',
  },
  data_transform: {
    transform_rule: 'Mapping Rules',
    name: 'Data Transform Name',
  },
  flow: {
    related_metrics: 'Output Metrics',
    name: 'Flow Name',
    description: 'Description',
    start_event: 'Initial Event',
    success_event: 'Complete Event',
    error_event: 'Error Event',
    threshold_t1: 'Gauge Setting-Poor',
    threshold_t2: 'Gauge Setting-Good',
    labels: 'Labels',
    without_event: 'Without Event',
  },
};

// The deployment page shows the diff keys
// Attention:
// 1. If it is an array, you need to write a separate rendering method for it.
// 2. DIVIDER means to insert a divider between two pieces of data。
export const metricDiffKeys = [
  'name',
  'category',
  'description',
  'metrics_type',
  'first_event',
  'second_event',
  'numerator',
  'denominator',
  'pairing_logic',
  'occurrence_type',
  'event',
  'calculation',
  ['within_range', 'within_unit'],
  'attribute',
  'is_higher_better',
  ['min_value', 'max_value'],
  'unit',
  'without_event',
];
export const mappedEventDiffKeys = [
  'related_metrics',
  DIVIDER,
  'rules',
  'event_name',
  'event_category',
  'description',
];
export const customDimensionDiffKeys = ['mapped_event_names', 'display_name', 'category'];
export const dataTransformDiffKeys = ['transform_rule', 'name'];
export const flowDiffKeys = [
  'related_metrics',
  DIVIDER,
  'name',
  'description',
  'labels',
  'start_event',
  'success_event',
  'error_event',
  ['timeout', 'timeout_unit'],
  ['distribution_min', 'distribution_max'],
  'threshold_t1',
  'threshold_t2',
  'without_event',
];

// keys that need to be rendered in related mode
export const relatedDiffKeys = ['related_metrics'];
// keys that need to be rendered in rules mode
export const rulesDiffKeys = ['mapped_event_names', 'rules', 'transform_rule'];
